import {Form, Input, message, Select} from "@ui";
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {usePaykuTransactionStatus, usePostPaykuDeposit} from "../../../hooks/wallet/usePostPaykuDeposit";
import useCurrencyStore from "../../../store/useCurrency.store";
import {PrimaryButton, SecondaryButton} from "../../Button";
import styles from "../DepositMethods.module.css";
import BaseModal from "@/components/BaseModal";

const {Option} = Select;

const PaykuPaymentModal = ({visible, onClose, paymentUrl, orderId, amount, handleModalClose}) => {
  const {data: transactionStatusResponse, error, isLoading} = usePaykuTransactionStatus(orderId, visible);
  const {t} = useTranslation();

  // Extract the actual transaction data from the nested response
  const transactionData = transactionStatusResponse?.data?.data;
  const actualStatus = transactionData?.status;

  useEffect(() => {
    // Check for successful status
    if (actualStatus === "approved" || actualStatus === "completed") {
      message.success(t("depositInstructionsPaykuPaymentCompleted"));
      setTimeout(() => {
        onClose();
      }, 3000);
    }

    if (actualStatus === "rejected") {
      message.error(t("depositInstructionsPaykuPaymentRejected"));
    }

    if (actualStatus === "expired") {
      message.warning(t("depositInstructionsPaykuPaymentExpired"));
    }

    if (error) {
    }
  }, [transactionStatusResponse, transactionData, actualStatus, onClose, error, t]);

  const handleOpenPayment = () => {
    if (paymentUrl) {
      window.open(paymentUrl, "_blank");
    }
  };

  return (
    <BaseModal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      title={t("depositInstructionsPaykuCompletePayment")}
      width={500}
    >
      <div style={{textAlign: "center", padding: "20px"}}>
        <div style={{marginBottom: "20px"}}>
          <h3 style={{marginBottom: "8px"}}>
            {t("depositInstructionsPaykuAmount")}: ${amount} CLP
          </h3>
          <p style={{color: "var(--text-secondary)", marginBottom: "20px"}}>{t("depositInstructionsPaykuOptions")}</p>
        </div>

        <div style={{marginBottom: "20px", width: "100%"}}>
          <PrimaryButton
            onClick={() => {
              handleOpenPayment();
              handleModalClose();
            }}
          >
            {t("depositInstructionsPaykuOpenLink")}
          </PrimaryButton>
        </div>

        <div style={{marginBottom: "15px"}}>
          <h4>{t("depositInstructionsPaykuStatus")}</h4>
          <p
            style={{
              color:
                actualStatus === "approved" || actualStatus === "completed"
                  ? "var(--accent-success)"
                  : actualStatus === "rejected"
                    ? "var(--status-error)"
                    : "var(--action-primary)",
            }}
          >
            {isLoading
              ? t("depositInstructionsPaykuCheckingStatus")
              : actualStatus
                ? t(`depositInstructionsPayku${actualStatus}`)
                : t("depositInstructionsPaykuPending")}
          </p>
        </div>

        <p style={{fontSize: "12px", color: "var(--text-secondary)"}}>{t("depositInstructionsPaykuInstructions")}</p>
      </div>
    </BaseModal>
  );
};

PaykuPaymentModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  paymentUrl: PropTypes.string,
  orderId: PropTypes.string,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleModalClose: PropTypes.func,
};

const PaykuOptions = ({onBack}) => {
  const {t} = useTranslation();
  const [form] = Form.useForm();
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(99); // Default to "All methods"
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const {selectedCurrency} = useCurrencyStore();
  const {mutate: createPaykuDeposit, isPending, isError, error} = usePostPaykuDeposit();

  // Payment methods for Chile (CLP)
  const CHILE_PAYMENT_METHODS = [
    {
      id: 99,
      name: t("depositInstructionsPaykuAllMethods"),
      description: t("depositInstructionsPaykuAllMethodsDescription"),
    },
    {id: 1, name: "Webpay", description: t("depositInstructionsPaykuWebpayDescription")},
    {id: 4, name: "Etpay", description: ""},
    {id: 6, name: "Pago46", description: ""},
    {id: 8, name: "Hites", description: ""},
    {id: 9, name: "Mach", description: ""},
    {id: 11, name: "Khipu", description: ""},
    {id: 15, name: "ServiFacil", description: ""},
    {id: 19, name: "Fintoc", description: ""},
    {
      id: 100,
      name: t("depositInstructionsPaykuWebpayPlus1_3"),
      description: t("depositInstructionsPaykuWebpayPlusDesc"),
    },
    {
      id: 101,
      name: t("depositInstructionsPaykuWebpayPlus4_6"),
      description: t("depositInstructionsPaykuWebpayPlusDesc"),
    },
    {
      id: 102,
      name: t("depositInstructionsPaykuWebpayPlus7_12"),
      description: t("depositInstructionsPaykuWebpayPlusDesc"),
    },
  ];

  // Payment methods for Peru (PEN) - Future support
  // const PERU_PAYMENT_METHODS = [
  //   {id: 21, name: "QR Interoperable", description: "Yape, Plin, and other apps"},
  // ];

  // Only show Payku for CLP currency
  const isValidCurrency = selectedCurrency?.id === "CLP";

  if (!isValidCurrency) {
    return (
      <div style={{textAlign: "center", padding: "2rem"}}>
        <h3>{t("depositInstructionsPaykuUnsupportedCurrency")}</h3>
        <p>{t("depositInstructionsPaykuOnlyCLP")}</p>
        <div style={{marginTop: "1rem"}}>
          <SecondaryButton onClick={onBack}>{t("depositInstructionsBack")}</SecondaryButton>
        </div>
      </div>
    );
  }

  const MIN_DEPOSIT = 5000;
  const validateForm = () => {
    const errors = [];

    if (!amount || Number(amount) < MIN_DEPOSIT) {
      errors.push(t("depositInstructionsPaykuMinAmountCustom", {min: MIN_DEPOSIT}));
    }

    if (!paymentMethod) {
      errors.push(t("depositInstructionsPaykuSelectMethod"));
    }

    return errors;
  };

  const handleSubmit = () => {
    const validationErrors = validateForm();

    if (validationErrors.length > 0) {
      for (const error of validationErrors) {
        message.error(error);
      }
      return;
    }

    const depositData = {
      amount: Number(amount),
      paymentMethod: paymentMethod,
      currency: "CLP",
    };

    createPaykuDeposit(depositData, {
      onSuccess: response => {
        if (response.data?.status === 1 && response.data?.data) {
          setPaymentData({
            orderId: response.data.data.orderId,
            paymentUrl: response.data.data.payment_url,
            amount: amount,
          });

          setIsModalVisible(true);
        } else {
          message.error(response.data?.message || t("depositInstructionsPaykuError"));
        }
      },
      onError: error => {
        // Custom error for min deposit
        if (Number(amount) < MIN_DEPOSIT) {
          message.error(t("depositInstructionsPaykuMinAmountCustom", {min: MIN_DEPOSIT}));
        } else if (error?.response?.status === 500) {
          message.error(t("depositInstructionsPaykuFailedTransactionMin", {min: MIN_DEPOSIT}));
        } else {
          message.error(error.message || t("depositInstructionsPaykuError"));
        }
      },
    });
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setPaymentData(null);
    // Reset form
    setAmount("");
    setPaymentMethod(99);
    form.resetFields();
  };

  return (
    <div>
      <div style={{marginBottom: "2rem"}}>
        <h3>{t("depositInstructionsPaykuTitle")}</h3>
        <p style={{color: "var(--text-secondary)", marginBottom: "1rem"}}>{t("depositInstructionsPaykuDescription")}</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{maxWidth: "400px", margin: "0 auto"}}
      >
        <Form.Item
          label={<span style={{fontWeight: "500", fontSize: "14px"}}>{t("depositInstructionsPaykuAmount")}</span>}
          required
          style={{marginBottom: "24px"}}
          className={styles.paykuAmountInput}
        >
          <Input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder={t("depositInstructionsPaykuEnterAmount")}
            addonAfter="CLP"
            size="large"
            min={MIN_DEPOSIT}
            style={{borderRadius: "6px"}}
          />
          <div style={{fontSize: "12px", color: "var(--text-secondary)", marginTop: "8px", fontStyle: "italic"}}>
            {t("depositInstructionsPaykuMinAmount", {min: MIN_DEPOSIT})}
          </div>
        </Form.Item>

        <Form.Item
          label={
            <span style={{fontWeight: "500", fontSize: "14px"}}>{t("depositInstructionsPaykuPaymentMethod")}</span>
          }
          required
          style={{marginBottom: "24px"}}
        >
          <Select
            value={paymentMethod}
            onChange={setPaymentMethod}
            size="large"
            placeholder={t("depositInstructionsPaykuSelectMethod")}
            style={{borderRadius: "6px"}}
          >
            {CHILE_PAYMENT_METHODS.map(method => (
              <Option
                key={method.id}
                value={method.id}
              >
                <div style={{padding: "2px 0"}}>
                  <div style={{fontWeight: "600", fontSize: "13px", lineHeight: "1.2"}}>{method.name}</div>
                  {method.description && (
                    <div
                      style={{fontSize: "10px", color: "var(--text-secondary)", lineHeight: "1.2", marginTop: "1px"}}
                    >
                      {method.description}
                    </div>
                  )}
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{display: "flex", gap: "0.5rem", marginTop: "2rem", justifyContent: "center"}}>
          <SecondaryButton onClick={onBack}>{t("depositInstructionsBack")}</SecondaryButton>
          <PrimaryButton
            htmlType="submit"
            loading={isPending}
          >
            {t("depositInstructionsPaykuCreatePayment")}
          </PrimaryButton>
        </div>

        {isError && error && (
          <div style={{marginTop: "1rem", color: "var(--accent-danger)", textAlign: "center"}}>
            {error.message || t("depositInstructionsPaykuError")}
          </div>
        )}
      </Form>

      {paymentData && (
        <PaykuPaymentModal
          visible={isModalVisible}
          onClose={handleModalClose}
          paymentUrl={paymentData.paymentUrl}
          orderId={paymentData.orderId}
          amount={paymentData.amount}
          handleModalClose={handleModalClose}
        />
      )}
    </div>
  );
};

PaykuOptions.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default PaykuOptions;
